/**
 * Holds the form for adding new comments and replies.
 * Refer to CommentPopup.tsx for more overview details.
 */
import { FC } from 'react'

import { Button, HStack, IconButton, Input, Spacer, Text, Textarea } from '@chakra-ui/react'
import { AddImageIcon, ConfirmIcon, InputEditorCancelIcon } from '@editor/assets/icons'
import { IMAGE_UPLOAD_STATUS } from '@editor/interfaces/comment'
import { RootState } from '@editor/store/app'
import { isMobile } from 'react-device-detect'
import { useSelector } from 'react-redux'

import { CommentInputProps, useCommentInput } from '../hooks/commentInput'
import { CommentInputImage } from './CommentImageInput'

/**
 * TODO: These does not belong here. Use Chakra-UI's theme instead.
 */
const ICON_BUTTON_PROPS = {
  boxSize: 8,
}
const BUTTON_STYLE = {
  width: 'auto',
  lineHeight: 1.2,
  height: '32px',
  fontWeight: 'bold',
  borderRadius: 'var(--chakra-radii-md)',
  paddingX: '12px',
}
const PRIMARY_BUTTON_STYLE = {
  ...BUTTON_STYLE,
  backgroundColor: '{colors.primary.500}',
  color: 'white',
  _hover: { backgroundColor: '{colors.primary.600}' },
  _disabled: { backgroundColor: '{colors.primary.300}' },
}
const SECONDARY_BUTTON_STYLE = {
  ...BUTTON_STYLE,
  borderColor: '{colors.gray2.200}',
  borderWidth: 1,
  _hover: { backgroundColor: '{colors.gray2.100}' },
  _disabled: { backgroundColor: 'white', color: '{colors.gray2.400}' },
}

export const CommentInput: FC<CommentInputProps> = (props) => {
  const { isDisabled, onCancel } = props
  const {
    commentName,
    commentBody,
    loading,
    isNewComment,
    isNewReply,
    updateCommentName,
    updateCommentBody,
    onMultipleFilesChange,
    handleSave,
    handleCancel,
    uploadMultipleFilesRef,
    textareaRef,
  } = useCommentInput(props)

  // Store
  const formImages = useSelector((state: RootState) => state.comments.formImages)

  return (
    <>
      {/* Show author's name field if it's a new comment/reply or just text if it's editing */}
      {isNewComment || isNewReply ? (
        <Input
          size="sm"
          placeholder="名前"
          value={commentName}
          onChange={(e) => updateCommentName(e.target.value)}
          maxLength={30}
          disabled={loading}
          borderWidth="1px"
          borderRadius="6px"
          padding="4px"
          width="100%"
        />
      ) : (
        <Text color="secondary.400" fontWeight="bold" as="span" marginBottom={-0.5} alignSelf="flex-start">
          {commentName}
        </Text>
      )}

      <Textarea
        ref={textareaRef}
        size="sm"
        placeholder="コメント"
        resize="vertical"
        value={commentBody}
        onChange={(e) => updateCommentBody(e.target.value)}
        maxLength={300}
        disabled={loading}
        borderWidth="1px"
        borderRadius="6px"
        padding="4px"
        width="100%"
        minHeight="50px"
      />

      <Input
        data-testid="multiple-file-upload"
        hidden
        ref={uploadMultipleFilesRef}
        type="file"
        accept={isMobile ? '' : '.jpg,.jpeg,.png,.svg'}
        onChange={onMultipleFilesChange}
        multiple
      />

      {formImages
        ?.filter((image) => image.status !== IMAGE_UPLOAD_STATUS.DELETING)
        .map((image) => (
          <CommentInputImage key={image.image_id} image={image} isDisabled={isDisabled} />
        ))}

      {/* ## Buttons for adding new reply ##  */}
      {isNewReply && (
        <>
          <IconButton
            {...ICON_BUTTON_PROPS}
            variant="outline"
            alignSelf="flex-start"
            aria-label="Add Image"
            disabled={isDisabled || loading}
            onClick={() => {
              uploadMultipleFilesRef.current?.click()
            }}
          >
            <AddImageIcon />
          </IconButton>
          <HStack alignSelf="stretch" pt={0} w="100%">
            <Button
              colorPalette="blue"
              size="sm"
              onClick={() => handleSave()}
              disabled={isDisabled || loading}
              {...PRIMARY_BUTTON_STYLE}
            >
              返信を送る
            </Button>
            <Spacer />
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!isDisabled) onCancel()
              }}
              disabled={isDisabled || loading}
              {...SECONDARY_BUTTON_STYLE}
            >
              閉じる
            </Button>
          </HStack>
        </>
      )}

      {/* ## Buttons for adding new comment ##  */}
      {isNewComment && (
        <>
          <IconButton
            {...ICON_BUTTON_PROPS}
            variant="outline"
            alignSelf="flex-start"
            aria-label="Add Image"
            disabled={isDisabled || loading}
            onClick={() => {
              uploadMultipleFilesRef.current?.click()
            }}
          >
            <AddImageIcon />
          </IconButton>
          <HStack width="100%" justifyContent="end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!isDisabled) onCancel()
              }}
              disabled={isDisabled || loading}
              {...SECONDARY_BUTTON_STYLE}
            >
              キャンセル
            </Button>
            <Button
              colorPalette="blue"
              size="sm"
              onClick={() => handleSave()}
              disabled={isDisabled || loading}
              {...PRIMARY_BUTTON_STYLE}
            >
              追加
            </Button>
          </HStack>
        </>
      )}

      {/* ## Buttons for editing comment ## */}
      {!isNewComment && !isNewReply && (
        <HStack width="100%">
          <IconButton
            {...ICON_BUTTON_PROPS}
            variant="outline"
            aria-label="Add Image"
            disabled={isDisabled || loading}
            onClick={() => {
              uploadMultipleFilesRef.current?.click()
            }}
          >
            <AddImageIcon />
          </IconButton>
          <Spacer />
          <IconButton
            {...ICON_BUTTON_PROPS}
            aria-label="Cancel Edit"
            disabled={isDisabled || loading}
            onClick={() => {
              if (!isDisabled) {
                handleCancel()
                onCancel()
              }
            }}
          >
            <InputEditorCancelIcon />
          </IconButton>
          <IconButton
            {...ICON_BUTTON_PROPS}
            aria-label="Confirm Edit"
            disabled={isDisabled || loading}
            onClick={() => handleSave()}
          >
            <ConfirmIcon />
          </IconButton>
        </HStack>
      )}
    </>
  )
}
